// Word-prefix, stopword-aware text matching. Mirrors the backend's FULLTEXT
// search contract (backend/api/services/makerSearchService/index.ts) so any
// client-side matching — the deepSearch fallback, or a local list filter —
// behaves consistently with a live search. Keep MIN_TOKEN_LEN + STOPWORDS in
// sync with that service.
const MIN_TOKEN_LEN = 3;
const STOPWORDS = new Set([
  "a",
  "about",
  "an",
  "are",
  "as",
  "at",
  "be",
  "by",
  "com",
  "de",
  "en",
  "for",
  "from",
  "how",
  "i",
  "in",
  "is",
  "it",
  "la",
  "of",
  "on",
  "or",
  "that",
  "the",
  "this",
  "to",
  "was",
  "what",
  "when",
  "where",
  "who",
  "will",
  "with",
  "und",
  "www",
]);

export function normalise(str: string) {
  return (str || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(new RegExp("[\\u0300-\\u036F]", "g"), "") // strip diacritic combining marks
    .replace(/['`’]/g, ""); // strip straight + smart apostrophes
}

export function tokenize(str: string): string[] {
  return normalise(str)
    .split(/[^a-z0-9]+/)
    .filter(Boolean);
}

// The words a search would actually require: >= the FULLTEXT min token length
// and not a stopword. Empty when nothing searchable remains. For the *backend*
// search contract (useDeepSearch) — MySQL's FULLTEXT index can't match tokens
// under MIN_TOKEN_LEN, so this mirrors that constraint.
export function searchWords(term: string): string[] {
  return tokenize(term).filter((w) => w.length >= MIN_TOKEN_LEN && !STOPWORDS.has(w));
}

// Stopword-aware, but no minimum length — for filtering an already-loaded
// local list (e.g. the premium change-maker search box), which never touches
// the backend's FULLTEXT index, so there's no reason to require 3+ characters
// before a query starts matching. Still drops stopwords so a query like
// "the pizza" isn't required to literally match "the" as a token.
export function localSearchWords(term: string): string[] {
  return tokenize(term).filter((w) => !STOPWORDS.has(w));
}

// Every search word must be a prefix of some token in the text — mirrors the
// backend's per-word `+word*` BOOLEAN MODE clauses (AND across words, prefix
// per word). Substring matches (e.g. "the" in "ga·the·r") do NOT count.
export function matchesWords(text: string, words: string[]): boolean {
  const tokens = tokenize(text);
  return words.every((w) => tokens.some((t) => t.startsWith(w)));
}
